import { useSocialStore } from "../../../stores/social";
import { Input } from "../../ui/inputs/Input";
import {  updatePayrollSettings, updatePayrollSettingsByCustomer } from "../../../api/payroll";
import { showToastError, showToastSuccess } from "../../../services/toast";
import { useUserContext } from "../../../contexts/userContext";
import { useParams } from "react-router-dom";
import { Checkbox } from "../../ui/inputs/Checkbox";
import { useEffect } from "react";
import { ModalButton, ModalShell } from "../../ui/modals/ModalShell";

const RateAndCeiling = ({ closeModal }: { closeModal: () => void }) => {
    const { formData, setFormData, formDataByCustomer, setFormDataByCustomer } = useSocialStore();
    const { typeRole } = useUserContext();
    const { idCustomer } = useParams();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    };
    const handleChangeByCustomer = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormDataByCustomer({ ...formDataByCustomer, [e.target.name]: e.target.value });
    };

    const handleChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormDataByCustomer({ ...formDataByCustomer, [e.target.name]: e.target.checked });
    };

    const sectionCardClass =
    "w-full rounded-xl border border-slate-200 bg-white"

    const formatAmount = (value: any): string => {
      if (value === null || value === undefined || value === "") return "";
      const numValue = typeof value === "number" ? value : parseFloat(value);
      if (isNaN(numValue)) return "";
      return `${numValue.toLocaleString('fr-FR')} €`;
    };

    // Fonction pour convertir les valeurs en nombres ou null
    const transformFormData = (data: Record<string, any>): Record<string, any> => {
      const transformed: Record<string, any> = {};
      
      for (const [key, value] of Object.entries(data)) {
        // Si la valeur est null ou undefined, on la garde telle quelle
        if (value === null || value === undefined) {
          transformed[key] = null;
        }
        // Si c'est une chaîne vide, on la convertit en null
        else if (value === "") {
          transformed[key] = null;
        }
        // Si c'est déjà un booléen, on le garde tel quel
        else if (typeof value === "boolean") {
          transformed[key] = value;
        }
        // Si c'est déjà un nombre, on le garde tel quel
        else if (typeof value === "number") {
          transformed[key] = value;
        }
        // Si c'est une chaîne, on essaie de la convertir en nombre
        else if (typeof value === "string") {
          // Si c'est une chaîne vide après trim, on met null
          const trimmed = value.trim();
          if (trimmed === "") {
            transformed[key] = null;
          } else {
            // Remplacer les virgules par des points pour gérer le format français
            const normalizedValue = trimmed.replace(/,/g, '.');
            // On essaie de convertir en nombre
            const numValue = parseFloat(normalizedValue);
            // Si c'est un nombre valide, on l'utilise, sinon on garde la chaîne originale
            if (!isNaN(numValue) && isFinite(numValue)) {
              transformed[key] = numValue;
            } else {
              // Si ce n'est pas un nombre, on garde la chaîne originale
              transformed[key] = trimmed;
            }
          }
        }
        // Pour les autres types (objets, tableaux, etc.), on les garde tels quels
        else {
          transformed[key] = value;
        }
      }
      
      return transformed;
    };

    const handleSubmit = () => {
      if (typeRole === "Company") {
        const transformedFormData = transformFormData(formData);
        updatePayrollSettings(transformedFormData)
          .then(() => {
            closeModal();
          })
          .catch((err) => {
            showToastError(err.response.data.message);
          })
      }
      
      const transformedFormDataByCustomer = transformFormData(formDataByCustomer);

      updatePayrollSettingsByCustomer(Number(idCustomer), transformedFormDataByCustomer)
        .then(() => {
           showToastSuccess("Taux et plafonds mis à jour avec succès");
           closeModal();
        })
        .catch((err) => {
           showToastError(err.response.data.message);
        })
    }

    useEffect(() => {
      const updates: Partial<typeof formDataByCustomer> = {};

      // Si CCSS assujetti à la CGCS est coché, on désactive le champ CCSS taux réduit
      if (formDataByCustomer.CCSSCGCS) {
        updates.CCSSReduced = false;
      } else if (formDataByCustomer.CCSSReduced) {
        // Si CCSS taux réduit est coché, on désactive le champ CCSS assujetti à la CGCS
        updates.CCSSCGCS = false;
      }

      // Si l'administrateur SAM est coché, on désactive le champ CCSSHousehold
      if (formDataByCustomer.administrateurSAM) {
        updates.CCSSHousehold = false;
      } else if (formDataByCustomer.CCSSHousehold) {
        // Si le gens de maison est coché, on désactive le champ administrateurSAM
        updates.administrateurSAM = false;
        // On active aussi CCSS taux réduit
        updates.CCSSReduced = true;
        // On désactive aussi CCSS assujetti à la CGCS
        updates.CCSSCGCS = false;
      }
      
      // Si l'affiliation CCPB Etam Cadres est cochée, on désactive le champ AffilCCPB_Ouvriers
      if (formDataByCustomer.AffilCCPB_EtamCadres) {
        updates.AffilCCPB_Ouvriers = false;
      } else if (formDataByCustomer.AffilCCPB_Ouvriers) {
        updates.AffilCCPB_EtamCadres = false;
      }
      
      // Appliquer toutes les mises à jour en une seule fois
      if (Object.keys(updates).length > 0) {
        setFormDataByCustomer({ ...formDataByCustomer, ...updates });
      }
      
    }, [formDataByCustomer.administrateurSAM, formDataByCustomer.CCSSHousehold, formDataByCustomer.CCSSCGCS, formDataByCustomer.CCSSReduced, formDataByCustomer.AffilCCPB_EtamCadres, formDataByCustomer.AffilCCPB_Ouvriers]);
    
  return (
    <ModalShell
      title="Taux et plafonds"
      subtitle="Complétez les informations des taux et plafonds du simulateur de paie."
      size="lg"
      onClose={() => closeModal()}
      footer={
        <>
          <ModalButton variant="secondary" onClick={() => closeModal()}>
            Annuler
          </ModalButton>
          <ModalButton
            variant="primary"
            type="submit"
            form="rate-and-ceiling-form"
          >
            Enregistrer
          </ModalButton>
        </>
      }
    >
      <form
        id="rate-and-ceiling-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="font-Nunito text-slate-700"
      >
          <div className="flex flex-col gap-4">
            <section className={`${sectionCardClass} p-5`}>
              <div className="grid grid-cols-3 gap-4 w-full items-center">
                <p className="text-sm text-grey col-span-2">
                  HORAIRES TEMPS PLEIN MONÉGASQUE
                </p>
                <Input
                  label=""
                  name="horaire_temps_plein_mon_gasque_hours"
                  value={formData.horaire_temps_plein_mon_gasque_hours || ""}
                  onChange={handleChange}
                  disabled={true}
                  clasName="col-span-1"
                />
              </div>
              <div className="grid grid-cols-3 gap-4 w-full items-center mt-1">
                <p className="text-sm text-grey col-span-2">
                  HORAIRES TEMPS PLEIN CMRC
                </p>
                <Input
                  label=""
                  name="horaire_temps_plein_cmrc_hours"
                  disabled={true}
                  value={formData.horaire_temps_plein_cmrc_hours || ""}
                  onChange={handleChange}
                  clasName="col-span-1"
                />
              </div>
              <div className="grid grid-cols-3 gap-4 w-full items-center mt-1">
                <p className="text-sm text-grey col-span-2">
                  SMIC HORAIRE
                </p>
                <Input
                  label=""
                  name="smic_amount"
                  disabled={typeRole !== "Company"}
                  value={formData.smic_amount || ""}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="€"
                />
              </div>
              <div className="grid grid-cols-3 gap-4 w-full items-center mt-1">
                <p className="text-sm text-grey col-span-2">
                  SMIC HORAIRE BRUT
                </p>
                <Input
                  label=""
                  name="smic_brut_amount"
                  disabled={typeRole !== "Company"}
                  value={formData.smic_brut_amount || ""}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="€"
                />
              </div>
            </section>

            <section className={`${sectionCardClass} p-5`}>
              <div className="grid grid-cols-5 gap-4 w-full items-center">
                <div className="col-span-3"></div>
                <p className="text-sm text-grey font-bold">Part Salariale %</p>
                <p className="text-sm text-grey font-bold">Part Patronale %</p>
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">CCSS (cgcs)</p>
                <Input
                  label=""
                  name="ccss__cgcs__amount"
                  value={formData.ccss__cgcs__amount || ""}
                  onChange={handleChange}
                  disabled={typeRole !== "Company"}
                  clasName="col-span-1"
                  suffix="€"
                />
                <div className="col-span-1"></div>
                <Input
                  label=""
                  name="ccss__cgcs__partPatronale"
                  value={formData.ccss__cgcs__partPatronale || ""}
                  disabled={typeRole !== "Company"}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">CCSS TAUX REDUIT</p>
                <div className="col-span-1"></div>
                <div className="col-span-1"></div>
                <Input
                  label=""
                  name="ccss_taux_r_duit_partPatronale"
                  disabled={typeRole !== "Company"}
                  value={formData.ccss_taux_r_duit_partPatronale || ""}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">CCSS GENS DE MAISON &lt; 254H</p>
                <Input
                  label=""
                  name="ccss_gens_de_maison___254h_amount"
                  disabled={typeRole !== "Company"}
                  value={formData.ccss_gens_de_maison___254h_amount || ""}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="€"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">CAR</p>
                <Input
                  label=""
                  name="car_amount"
                  value={formData.car_amount || ""}
                  disabled={typeRole !== "Company"}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="€"
                />
                <Input
                  label=""
                  name="car_partSalariale"
                  value={formData.car_partSalariale || ""}
                  disabled={typeRole !== "Company"}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
                <Input
                  label=""
                  name="car_partPatronale"
                  value={formData.car_partPatronale || ""}
                  disabled={typeRole !== "Company"}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">PLAFOND CMRC</p>
                <Input
                  label=""
                  name="plafond_cmrc_amount"
                  value={formData.plafond_cmrc_amount || ""}
                  disabled={typeRole !== "Company"}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="€"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">PLAFOND SS FRANCAIS</p>
                <Input
                  label=""
                  name="plafond_ss_francais_amount"
                  value={formData.plafond_ss_francais_amount || ""}
                  onChange={handleChange}
                  disabled={typeRole !== "Company"}
                  clasName="col-span-1"
                  suffix="€"
                />
              </div>

              <div className="mt-6 pt-6 border-t border-light-blue/30">
                <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-grey/70 mb-4">
                  Options de configuration
                </h4>
                <div className="grid grid-cols-1 gap-1 w-full items-center">
                  <Checkbox label="Forcer l'indemnité 5%" name="indemnite5" value="indemnite5" onChange={handleChangeCheckbox} checked={!!formDataByCustomer.indemnite5} />
                  <Checkbox label="Admin de SAM" name="administrateurSAM" value="administrateurSAM" onChange={handleChangeCheckbox} checked={!!formDataByCustomer.administrateurSAM} />
                  <Checkbox label="CCSS (assujetti à la CGCS)" name="CCSSCGCS" value="CCSSCGCS" onChange={handleChangeCheckbox} checked={!!formDataByCustomer.CCSSCGCS} />
                  <Checkbox label="CCSS taux réduit" name="CCSSReduced" value="CCSSReduced" onChange={handleChangeCheckbox} checked={!!formDataByCustomer.CCSSReduced} />
                  <Checkbox label="CCSS gens de maison (base à 33% du brut)" name="CCSSHousehold" value="CCSSHousehold" onChange={handleChangeCheckbox} checked={!!formDataByCustomer.CCSSHousehold} />
                </div>
              </div>
            </section>

            <section className={`${sectionCardClass} p-5`}>
              <div className="grid grid-cols-5 gap-4 w-full items-center">
                <div className="col-span-3"></div>
                <p className="text-sm text-grey font-bold">Part Salariale %</p>
                <p className="text-sm text-grey font-bold">Part Patronale %</p>
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">POLE EMPLOI</p>
                <p className="text-end pr-2">{formatAmount(formData.france_travail_amount)} </p>
                <Input
                  label=""
                  name="france_travail_partSalariale"
                  disabled={typeRole !== "Company"}
                  value={formData.france_travail_partSalariale || ""}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
                <Input
                  label=""
                  name="france_travail_partPatronale"
                  value={formData.france_travail_partPatronale || ""}
                  onChange={handleChange}
                  disabled={typeRole !== "Company"}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="my-3 border border-gray-200 rounded-md p-1 shadow-sm flex">
                <Checkbox label="Affil. Ass. Chômage" name="AffilAssChomage" value="AffilAssChomage" onChange={handleChangeCheckbox} checked={!!formDataByCustomer.AffilAssChomage} />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">CMRC TA</p>
                <p className="text-end pr-2">{formatAmount(formData.cmrc_ta_amount)}</p>
                <Input
                  label=""
                  name="cmrc_ta_partSalariale"
                  value={formData.cmrc_ta_partSalariale || ""}
                  disabled={typeRole !== "Company"}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
                <Input
                  label=""
                  name="cmrc_ta_partPatronale"
                  value={formData.cmrc_ta_partPatronale || ""}
                  disabled={typeRole !== "Company"}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>
              
              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">CMRC TB</p>
                <p className="text-end pr-2">{formatAmount(formData.cmrc_tb_amount)}</p>
                <Input
                  label=""
                  name="cmrc_tb_partSalariale"
                  disabled={typeRole !== "Company"}
                  value={formData.cmrc_tb_partSalariale || ""}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
                <Input
                  label=""
                  name="cmrc_tb_partPatronale"
                  value={formData.cmrc_tb_partPatronale || ""}
                  onChange={handleChange}
                  disabled={typeRole !== "Company"}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">CCPB CONGES + FORMATIONS</p>
                <p className="text-end pr-2"></p>
                <div className="col-span-1"></div>
                <Input
                  label=""
                  name="ccpb_cong_s___formation_partPatronale"
                  disabled={typeRole !== "Company"}
                  value={formData.ccpb_cong_s___formation_partPatronale || ""}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">CCPB PREVOYANCE</p>
                <p className="text-end pr-2"></p>
                <Input
                  label=""
                  name="ccpb_pr_voyance_partSalariale"
                  value={formData.ccpb_pr_voyance_partSalariale || ""}
                  disabled={typeRole !== "Company"}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
                <Input
                  label=""
                  name="ccpb_pr_voyance_partPatronale"
                  value={formData.ccpb_pr_voyance_partPatronale || ""}
                  disabled={typeRole !== "Company"}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>


              <div className="grid grid-cols-2 gap-2 w-full items-center my-2 border border-gray-200 rounded-md p-1 shadow-sm">
                <Checkbox label="Affil. CCPB Etam/Cadre" name="AffilCCPB_EtamCadres" value="AffilCCPB_EtamCadres" onChange={handleChangeCheckbox} checked={!!formDataByCustomer.AffilCCPB_EtamCadres} />
                <Checkbox label="Affil. CCPB Ouvrier" name="AffilCCPB_Ouvriers" value="AffilCCPB_Ouvriers" onChange={handleChangeCheckbox} checked={!!formDataByCustomer.AffilCCPB_Ouvriers} />
                <Checkbox label="Prév. T1 CCSS proratisé en h." name="PrevoyanceT1_CCSS" value="PrevoyanceT1_CCSS" onChange={handleChangeCheckbox} checked={!!formDataByCustomer.PrevoyanceT1_CCSS} />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">PREVOYANCE T1 CCSS</p>
                <p className="text-end pr-2">{formatAmount(formDataByCustomer.prevoyanceT1_Amount)}</p>
                <Input
                  label=""
                  name="prevoyanceT1_PartSalariale"
                  value={formDataByCustomer.prevoyanceT1_PartSalariale || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
                <Input
                  label=""
                  name="prevoyanceT1_PartPatronale"
                  value={formDataByCustomer.prevoyanceT1_PartPatronale || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>
              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">PREVOYANCE TA (Plaf. SS FR)</p>
                <p className="text-end pr-2">{formatAmount(formDataByCustomer.prevoyanceTA_Amount)} </p>
                <Input
                  label=""
                  name="prevoyanceTA_PartSalariale"
                  value={formDataByCustomer.prevoyanceTA_PartSalariale || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
                <Input
                  label=""
                  name="prevoyanceTA_PartPatronale"
                  value={formDataByCustomer.prevoyanceTA_PartPatronale || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">PREVOYANCE T2 CCSS</p>
                <p className="text-end pr-2">{formatAmount(formDataByCustomer.prevoyanceT2_Amount)}</p>
                <Input
                  label=""
                  name="prevoyanceT2_PartSalariale"
                  value={formDataByCustomer.prevoyanceT2_PartSalariale || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
                <Input
                  label=""
                  name="prevoyanceT2_PartPatronale"
                  value={formDataByCustomer.prevoyanceT2_PartPatronale || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">PREVOYANCE TB</p>
                <p className="text-end pr-2">{formatAmount(formDataByCustomer.prevoyanceTB_Amount)}</p>
                <Input
                  label=""
                  name="prevoyanceTB_PartSalariale"
                  value={formDataByCustomer.prevoyanceTB_PartSalariale || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
                <Input
                  label=""
                  name="prevoyanceTB_PartPatronale"
                  value={formDataByCustomer.prevoyanceTB_PartPatronale || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">PREVOYANCE TC</p>
                <p className="text-end pr-2">{formatAmount(formDataByCustomer.prevoyanceTC_Amount)}</p>
                <Input
                  label=""
                  name="prevoyanceTC_PartSalariale"
                  value={formDataByCustomer.prevoyanceTC_PartSalariale || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
                <Input
                  label=""
                  name="prevoyanceTC_PartPatronale"
                  value={formDataByCustomer.prevoyanceTC_PartPatronale || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">TAUX AT</p>
                <div className="col-span-1"></div>
                <div className="col-span-1"></div>
                <Input
                  label=""
                  name="tauxAT"
                  value={formDataByCustomer.tauxAT || ""}
                  onChange={handleChangeByCustomer}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">TAXE SUR COTISATION AT</p>
                <div className="col-span-1"></div>
                <div className="col-span-1"></div>
                <Input
                  label=""
                  name="taxe_sur_cotisation_at_partPatronale"
                  disabled={typeRole !== "Company"}
                  value={formData.taxe_sur_cotisation_at_partPatronale|| ""}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>

              <div className="grid grid-cols-5 gap-2 w-full items-center">
                <p className="text-sm text-grey col-span-2">FONDS SOCIAL</p>
                <div className="col-span-1"></div>
                <div className="col-span-1"></div>
                <Input
                  label=""
                  name="fonds_social_partPatronale"
                  value={formData.fonds_social_partPatronale || ""}
                  disabled={typeRole !== "Company"}
                  onChange={handleChange}
                  clasName="col-span-1"
                  suffix="%"
                />
              </div>
              <div className="my-3 border border-gray-200 rounded-md p-1 shadow-sm flex">
                <Checkbox label="Fonds social (+ 50 sal.)" name="FondsSocial" value="FondsSocial" onChange={handleChangeCheckbox} checked={!!formDataByCustomer.FondsSocial} />
              </div>

            </section>
          </div>
      </form>
    </ModalShell>
  )
}

export default RateAndCeiling
